import type {
  Block,
  BlockType,
  Column,
  Design,
  LeafBlock,
  RowBlock,
  TextStyle,
} from './types.js';

/**
 * Client-side design-document operations.
 *
 * All immutable: every edit returns a new document. That is what makes undo,
 * dirty-state detection and "save as a new version" straightforward, and it
 * matches the server, which treats each version as immutable once published.
 */

let counter = 0;

/** Stable-enough unique id for a new block. */
export function uid(prefix = 'b'): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

export function defaultTextStyle(over: Partial<TextStyle> = {}): TextStyle {
  return {
    fontSize: 13,
    bold: false,
    italic: false,
    color: '#3a3a3a',
    lineHeight: 1.4,
    align: 'left',
    letterSpacing: 0,
    transform: 'none',
    marginTop: 0,
    marginBottom: 0,
    ...over,
  };
}

export function newColumn(width: number): Column {
  return { id: uid('col'), width, valign: 'top', paddingRight: 0, blocks: [] };
}

/**
 * A new block of the given type, pre-filled with values that render sensibly.
 * Defaults matter here: a new image with no width, or a sparse field with no
 * guard, would immediately trip the linter.
 */
export function newBlock(type: BlockType): Block {
  switch (type) {
    case 'text':
      return {
        id: uid('text'),
        type: 'text',
        content: 'New line',
        style: defaultTextStyle(),
        hideIfEmpty: true,
      };
    case 'field':
      return {
        id: uid('field'),
        type: 'field',
        field: 'JobTitle',
        prefix: '',
        suffix: '',
        fallback: '',
        hideIfEmpty: true,
        link: 'none',
        style: defaultTextStyle(),
      };
    case 'image':
      return {
        id: uid('img'),
        type: 'image',
        kind: 'logo',
        src: 'https://placehold.co/280x80/0b5fff/ffffff.png?text=LOGO',
        alt: 'Company logo',
        width: 140,
        height: 40,
        align: 'left',
        marginTop: 0,
        marginBottom: 8,
      };
    case 'social':
      return {
        id: uid('social'),
        type: 'social',
        size: 24,
        gap: 8,
        align: 'left',
        marginTop: 4,
        marginBottom: 4,
        icons: [
          {
            network: 'linkedin',
            url: 'https://www.linkedin.com/company/example',
            src: 'https://placehold.co/48x48/0a66c2/ffffff.png?text=in',
            alt: 'LinkedIn',
          },
        ],
      };
    case 'divider':
      return {
        id: uid('div'),
        type: 'divider',
        color: '#d8d8d8',
        thickness: 1,
        width: 100,
        spacing: 10,
      };
    case 'spacer':
      return { id: uid('sp'), type: 'spacer', height: 12 };
    case 'disclaimer':
      return {
        id: uid('disc'),
        type: 'disclaimer',
        content:
          'This email and any attachments are confidential and intended solely for the addressee.',
        style: defaultTextStyle({ fontSize: 10, color: '#767676', lineHeight: 1.35 }),
      };
    case 'row':
      return {
        id: uid('row'),
        type: 'row',
        stackOnMobile: true,
        columns: [newColumn(30), newColumn(70)],
      };
  }
}

export const LEAF_TYPES: BlockType[] = [
  'text',
  'field',
  'image',
  'social',
  'divider',
  'spacer',
  'disclaimer',
];

export const BLOCK_LABELS: Record<BlockType, string> = {
  text: 'Text',
  field: 'Field',
  image: 'Image',
  social: 'Social',
  divider: 'Divider',
  spacer: 'Spacer',
  disclaimer: 'Disclaimer',
  row: 'Columns',
};

/** One-line description for the block list. */
export function blockLabel(block: Block): string {
  switch (block.type) {
    case 'text':
      return block.content.split('\n')[0]?.slice(0, 44) || '(empty text)';
    case 'field':
      return `${block.prefix}{{${block.field}}}${block.suffix}`.trim();
    case 'image':
      return block.alt || block.src.split('/').pop() || 'image';
    case 'social':
      return block.icons.length
        ? block.icons.map((i) => i.network).join(', ')
        : '(no icons)';
    case 'divider':
      return `${block.thickness}px line`;
    case 'spacer':
      return `${block.height}px gap`;
    case 'disclaimer':
      return block.content.slice(0, 44) || '(empty disclaimer)';
    case 'row':
      return `${block.columns.length} columns (${block.columns.map((c) => `${c.width}%`).join(' / ')})`;
  }
}

// --- Traversal -------------------------------------------------------------

export interface FlatEntry {
  block: Block;
  /** 0 for a top-level block, 1 for a block inside a column. */
  depth: number;
  /** Set when the block lives inside a row column. */
  parent?: { rowId: string; columnId: string; columnIndex: number };
  /** Index within its own sibling list. */
  index: number;
  siblingCount: number;
}

/** The block list, flattened for rendering as an outline. */
export function flatten(design: Design): FlatEntry[] {
  const out: FlatEntry[] = [];
  design.blocks.forEach((block, index) => {
    out.push({ block, depth: 0, index, siblingCount: design.blocks.length });
    if (block.type === 'row') {
      block.columns.forEach((column, columnIndex) => {
        column.blocks.forEach((child, childIndex) => {
          out.push({
            block: child,
            depth: 1,
            parent: { rowId: block.id, columnId: column.id, columnIndex },
            index: childIndex,
            siblingCount: column.blocks.length,
          });
        });
      });
    }
  });
  return out;
}

export function findBlock(design: Design, id: string): FlatEntry | null {
  return flatten(design).find((e) => e.block.id === id) ?? null;
}

// --- Mutation --------------------------------------------------------------

function mapBlocks(design: Design, fn: (b: Block) => Block): Design {
  return {
    ...design,
    blocks: design.blocks.map((block) => {
      if (block.type === 'row') {
        const mappedRow: RowBlock = {
          ...block,
          columns: block.columns.map((column) => ({
            ...column,
            blocks: column.blocks.map((child) => fn(child) as LeafBlock),
          })),
        };
        return fn(mappedRow);
      }
      return fn(block);
    }),
  };
}

/** Replaces one block by id, wherever it sits. */
export function updateBlock(design: Design, id: string, patch: Partial<Block>): Design {
  return mapBlocks(design, (block) =>
    block.id === id ? ({ ...block, ...patch } as Block) : block,
  );
}

/** Replaces one block by id using a function, for type-narrowed edits. */
export function replaceBlock(design: Design, id: string, next: Block): Design {
  return mapBlocks(design, (block) => (block.id === id ? next : block));
}

export function removeBlock(design: Design, id: string): Design {
  return {
    ...design,
    blocks: design.blocks
      .filter((block) => block.id !== id)
      .map((block) =>
        block.type === 'row'
          ? {
              ...block,
              columns: block.columns.map((column) => ({
                ...column,
                blocks: column.blocks.filter((child) => child.id !== id),
              })),
            }
          : block,
      ),
  };
}

/**
 * Appends a block. A leaf goes into the named column when one is given,
 * otherwise to the end of the top-level list. Rows are always top-level:
 * nesting a row inside a column produces layouts Outlook cannot render.
 */
export function addBlock(design: Design, block: Block, columnId?: string): Design {
  if (block.type === 'row' || !columnId) {
    return { ...design, blocks: [...design.blocks, block] };
  }
  return {
    ...design,
    blocks: design.blocks.map((b) =>
      b.type === 'row'
        ? {
            ...b,
            columns: b.columns.map((column) =>
              column.id === columnId
                ? { ...column, blocks: [...column.blocks, block as LeafBlock] }
                : column,
            ),
          }
        : b,
    ),
  };
}

function moveWithin<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return list;
  next.splice(to, 0, item);
  return next;
}

/** Moves a block up or down within its own sibling list. */
export function moveBlock(design: Design, id: string, delta: -1 | 1): Design {
  const topIndex = design.blocks.findIndex((b) => b.id === id);
  if (topIndex >= 0) {
    return { ...design, blocks: moveWithin(design.blocks, topIndex, topIndex + delta) };
  }

  return {
    ...design,
    blocks: design.blocks.map((block) => {
      if (block.type !== 'row') return block;
      return {
        ...block,
        columns: block.columns.map((column) => {
          const index = column.blocks.findIndex((c) => c.id === id);
          if (index < 0) return column;
          return { ...column, blocks: moveWithin(column.blocks, index, index + delta) };
        }),
      };
    }),
  };
}

/** Reorders top-level blocks, used by the drag-and-drop handler. */
export function reorderTopLevel(design: Design, fromId: string, toId: string): Design {
  const from = design.blocks.findIndex((b) => b.id === fromId);
  const to = design.blocks.findIndex((b) => b.id === toId);
  if (from < 0 || to < 0) return design;
  return { ...design, blocks: moveWithin(design.blocks, from, to) };
}

/** Duplicates a block next to itself, with fresh ids throughout. */
export function duplicateBlock(design: Design, id: string): Design {
  const entry = findBlock(design, id);
  if (!entry) return design;
  const copy = withFreshIds(entry.block);

  if (!entry.parent) {
    const index = design.blocks.findIndex((b) => b.id === id);
    const blocks = [...design.blocks];
    blocks.splice(index + 1, 0, copy);
    return { ...design, blocks };
  }

  const { columnId } = entry.parent;
  return {
    ...design,
    blocks: design.blocks.map((block) => {
      if (block.type !== 'row') return block;
      return {
        ...block,
        columns: block.columns.map((column) => {
          if (column.id !== columnId) return column;
          const index = column.blocks.findIndex((c) => c.id === id);
          const blocks = [...column.blocks];
          blocks.splice(index + 1, 0, copy as LeafBlock);
          return { ...column, blocks };
        }),
      };
    }),
  };
}

function withFreshIds(block: Block): Block {
  if (block.type === 'row') {
    return {
      ...block,
      id: uid('row'),
      columns: block.columns.map((column) => ({
        ...column,
        id: uid('col'),
        blocks: column.blocks.map((child) => ({ ...child, id: uid(child.type) })),
      })),
    };
  }
  return { ...block, id: uid(block.type) };
}

/** Rebalances a row's columns to total 100%, which the linter requires. */
export function normaliseColumns(design: Design, rowId: string): Design {
  return {
    ...design,
    blocks: design.blocks.map((block) => {
      if (block.type !== 'row' || block.id !== rowId) return block;
      const even = Math.floor(100 / block.columns.length);
      return {
        ...block,
        columns: block.columns.map((column, i) => ({
          ...column,
          width: i === block.columns.length - 1 ? 100 - even * (block.columns.length - 1) : even,
        })),
      };
    }),
  };
}

export function setColumnCount(design: Design, rowId: string, count: number): Design {
  return normaliseColumns(
    {
      ...design,
      blocks: design.blocks.map((block) => {
        if (block.type !== 'row' || block.id !== rowId) return block;
        const columns = [...block.columns];
        while (columns.length < count) columns.push(newColumn(0));
        while (columns.length > count) {
          const removed = columns.pop();
          // Never silently drop content: anything in a removed column moves to
          // the first column rather than disappearing.
          if (removed && removed.blocks.length && columns[0]) {
            columns[0] = { ...columns[0], blocks: [...columns[0].blocks, ...removed.blocks] };
          }
        }
        return { ...block, columns };
      }),
    },
    rowId,
  );
}

export function updateColumn(
  design: Design,
  columnId: string,
  patch: Partial<Omit<Column, 'id' | 'blocks'>>,
): Design {
  return {
    ...design,
    blocks: design.blocks.map((block) =>
      block.type === 'row'
        ? {
            ...block,
            columns: block.columns.map((column) =>
              column.id === columnId ? { ...column, ...patch } : column,
            ),
          }
        : block,
    ),
  };
}

/** Every column in the document, for the "add block to" picker. */
export function allColumns(design: Design): Array<{ rowId: string; column: Column; label: string }> {
  const out: Array<{ rowId: string; column: Column; label: string }> = [];
  design.blocks.forEach((block, rowIndex) => {
    if (block.type !== 'row') return;
    block.columns.forEach((column, i) => {
      out.push({
        rowId: block.id,
        column,
        label: `Row ${rowIndex + 1}, column ${i + 1} (${column.width}%)`,
      });
    });
  });
  return out;
}

/** Structural equality, used for the unsaved-changes indicator. */
export function designsEqual(a: Design, b: Design): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
